import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useAdvisors } from '../../hooks/useAdvisors'
import { useRoles } from '../../hooks/useRoles'
import { useAuthAdmin } from '../../hooks/useAuthAdmin'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'
import { AdvisorAvatar } from '../../components/Common/AdvisorAvatar'
import { Mail, KeyRound, CheckCircle, AlertCircle, QrCode, Copy } from 'lucide-react'

const LICENCE_TYPES = [
  'Key Individual',
  'Representative',
  'Category I FSP',
  'Category II FSP',
  'Category IIA FSP',
  'Category III FSP',
]

const TABS = [
  { id: 'details', label: 'Details' },
  { id: 'roles', label: 'Roles' },
  { id: 'auth', label: 'Authentication' },
]

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? 'bg-sfp-orange' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

export function UserForm({ initialData, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('details')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [savedAdvisorId, setSavedAdvisorId] = useState(initialData?.id || null)
  const [assignedRoles, setAssignedRoles] = useState(new Set())
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarPreview, setAvatarPreview] = useState(initialData?.avatar_url ?? null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [originalEmail] = useState(initialData?.email || '')
  const [sendingInvite, setSendingInvite] = useState(false)
  const [sendingReset, setSendingReset] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [lastInviteTime, setLastInviteTime] = useState(null)
  const [secondsUntilNextInvite, setSecondsUntilNextInvite] = useState(0)
  const [lastResetTime, setLastResetTime] = useState(null)
  const [secondsUntilNextReset, setSecondsUntilNextReset] = useState(0)
  const [generatingQR, setGeneratingQR] = useState(false)
  const [signInLink, setSignInLink] = useState(null)
  const [lastQRTime, setLastQRTime] = useState(null)
  const [secondsUntilNextQR, setSecondsUntilNextQR] = useState(0)

  const [formData, setFormData] = useState({
    full_name: initialData?.full_name || '',
    email: initialData?.email || '',
    mobile: initialData?.mobile || '',
    licence_type: initialData?.licence_type || '',
    licence_number: initialData?.licence_number || '',
    active: initialData?.active ?? true,
  })

  const { createAdvisor, updateAdvisor } = useAdvisors()
  const { roles, loading: rolesLoading, assignAdvisor, unassignAdvisor } = useRoles()
  const { inviteUser, updateUserEmail, sendPasswordReset, generateSignInLink } = useAuthAdmin()
  const { advisor: currentAdvisor } = useCurrentAdvisor()
  const { canAccess } = useAuth()
  const isOwner = currentAdvisor?.is_owner === true || canAccess('users_manage')

  // Load existing role assignments
  useEffect(() => {
    if (!savedAdvisorId) return
    supabase
      .from('advisor_roles')
      .select('role_id')
      .eq('advisor_id', savedAdvisorId)
      .then(({ data }) => {
        setAssignedRoles(new Set(data?.map(r => r.role_id) || []))
      })
  }, [savedAdvisorId])

  // Rate limit countdown timer (60 seconds between invites)
  useEffect(() => {
    if (!lastInviteTime) {
      setSecondsUntilNextInvite(0)
      return
    }

    const calculateRemaining = () => {
      const now = Date.now()
      const elapsedSeconds = Math.floor((now - lastInviteTime) / 1000)
      const remaining = Math.max(0, 60 - elapsedSeconds)
      setSecondsUntilNextInvite(remaining)
      return remaining
    }

    calculateRemaining()
    const interval = setInterval(() => {
      const remaining = calculateRemaining()
      if (remaining === 0) {
        clearInterval(interval)
        setLastInviteTime(null)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [lastInviteTime])

  // Rate limit countdown timer (60 seconds between password resets)
  useEffect(() => {
    if (!lastResetTime) {
      setSecondsUntilNextReset(0)
      return
    }

    const calculateRemaining = () => {
      const now = Date.now()
      const elapsedSeconds = Math.floor((now - lastResetTime) / 1000)
      const remaining = Math.max(0, 60 - elapsedSeconds)
      setSecondsUntilNextReset(remaining)
      return remaining
    }

    calculateRemaining()
    const interval = setInterval(() => {
      const remaining = calculateRemaining()
      if (remaining === 0) {
        clearInterval(interval)
        setLastResetTime(null)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [lastResetTime])

  // Rate limit countdown timer (60 seconds between QR code generations)
  useEffect(() => {
    if (!lastQRTime) {
      setSecondsUntilNextQR(0)
      return
    }

    const calculateRemaining = () => {
      const now = Date.now()
      const elapsedSeconds = Math.floor((now - lastQRTime) / 1000)
      const remaining = Math.max(0, 60 - elapsedSeconds)
      setSecondsUntilNextQR(remaining)
      return remaining
    }

    calculateRemaining()
    const interval = setInterval(() => {
      const remaining = calculateRemaining()
      if (remaining === 0) {
        clearInterval(interval)
        setLastQRTime(null)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [lastQRTime])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
    e.target.value = ''
  }

  const uploadAvatar = async (advisorId) => {
    if (!avatarFile || !advisorId) return
    setAvatarUploading(true)
    try {
      const ext = avatarFile.name.split('.').pop() || 'jpg'
      const filename = `${advisorId}.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(filename, avatarFile, { upsert: true })
      if (uploadErr) throw uploadErr
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filename)
      await supabase.from('advisors').update({ avatar_url: urlData.publicUrl }).eq('id', advisorId)
    } catch (err) {
      console.error('Avatar upload failed:', err.message)
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleSave = async () => {
    if (!formData.full_name.trim()) { setError('Full name is required'); return }
    if (!formData.email.trim()) { setError('Email is required'); return }
    setError('')
    setSuccessMessage('')
    setSaving(true)
    try {
      if (savedAdvisorId) {
        // Update existing advisor
        await updateAdvisor(savedAdvisorId, {
          full_name: formData.full_name.trim(),
          email: formData.email.trim(),
          mobile: formData.mobile,
          licence_type: formData.licence_type || null,
          licence_number: formData.licence_number || null,
          active: formData.active,
        })
        // If email changed, sync to Supabase Auth
        if (originalEmail && originalEmail !== formData.email.trim() && isOwner) {
          try {
            await updateUserEmail({
              old_email: originalEmail,
              new_email: formData.email.trim(),
              advisor_id: savedAdvisorId,
            })
            setSuccessMessage('User updated and email synced to authentication.')
          } catch (emailErr) {
            console.warn('Email sync failed:', emailErr.message)
            // Don't fail the whole save - just warn
            setSuccessMessage('User updated. Note: Email sync to auth failed - user may need a new invite.')
          }
        }
      } else {
        // Create new advisor
        const created = await createAdvisor({
          full_name: formData.full_name.trim(),
          email: formData.email.trim(),
          mobile: formData.mobile,
          licence_type: formData.licence_type || null,
          licence_number: formData.licence_number || null,
          active: formData.active,
        })
        setSavedAdvisorId(created.id)
        if (avatarFile) await uploadAvatar(created.id)

        // Send invite email if owner
        if (isOwner) {
          try {
            await inviteUser({
              email: formData.email.trim(),
              full_name: formData.full_name.trim(),
              advisor_id: created.id,
            })
            setInviteSent(true)
            setSuccessMessage('User created and invitation email sent!')
          } catch (inviteErr) {
            console.warn('Invite failed:', inviteErr.message)
            setSuccessMessage('User created. Note: Invitation email failed - you can send it from the Authentication tab.')
          }
        }
      }
      if (avatarFile && savedAdvisorId) await uploadAvatar(savedAdvisorId)
      setActiveTab('roles')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvite = async () => {
    if (!savedAdvisorId || !formData.email) return
    if (secondsUntilNextInvite > 0) return
    setLastInviteTime(Date.now())
    setSendingInvite(true)
    setError('')
    setSuccessMessage('')
    try {
      await inviteUser({
        email: formData.email.trim(),
        full_name: formData.full_name.trim(),
        advisor_id: savedAdvisorId,
      })
      setInviteSent(true)
      setSuccessMessage('Invitation email sent successfully!')
    } catch (err) {
      if (err.message?.toLowerCase().includes('rate limit')) {
        setLastInviteTime(Date.now())
      }
      setError(err.message)
    } finally {
      setSendingInvite(false)
    }
  }

  const handleSendPasswordReset = async () => {
    if (!formData.email) return
    if (secondsUntilNextReset > 0) return
    setLastResetTime(Date.now())
    setSendingReset(true)
    setError('')
    setSuccessMessage('')
    try {
      await sendPasswordReset(formData.email.trim())
      setSuccessMessage('Password reset email sent successfully!')
    } catch (err) {
      if (err.message?.toLowerCase().includes('rate limit')) {
        setLastResetTime(Date.now())
      }
      setError(err.message)
    } finally {
      setSendingReset(false)
    }
  }

  const handleGenerateQRCode = async () => {
    if (!formData.email) return
    if (secondsUntilNextQR > 0) return
    setLastQRTime(Date.now())
    setGeneratingQR(true)
    setError('')
    try {
      const link = await generateSignInLink(formData.email.trim())
      setSignInLink(link)
      setSuccessMessage('Sign-in link generated! User can scan the QR code to log in.')
    } catch (err) {
      if (err.message?.toLowerCase().includes('rate limit')) {
        setLastQRTime(Date.now())
      }
      setError(err.message)
    } finally {
      setGeneratingQR(false)
    }
  }

  const copyLinkToClipboard = () => {
    if (signInLink) {
      navigator.clipboard.writeText(signInLink)
      setSuccessMessage('Link copied to clipboard!')
    }
  }

  const handleRoleToggle = async (roleId, isAssigned) => {
    const next = new Set(assignedRoles)
    if (isAssigned) {
      next.delete(roleId)
      setAssignedRoles(next)
      try { await unassignAdvisor(roleId, savedAdvisorId) }
      catch (err) { setAssignedRoles(assignedRoles); console.error(err) }
    } else {
      next.add(roleId)
      setAssignedRoles(next)
      try { await assignAdvisor(roleId, savedAdvisorId) }
      catch (err) { setAssignedRoles(assignedRoles); console.error(err) }
    }
  }

  const notSavedYet = !savedAdvisorId

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={savedAdvisorId ? `Edit User: ${formData.full_name}` : 'Add User'}
      sidebarCollapsed={sidebarCollapsed}
      isDarkMode={isDarkMode}
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => !notSavedYet || tab.id === 'details' ? setActiveTab(tab.id) : null}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab.id
                ? 'border-sfp-orange text-sfp-orange'
                : notSavedYet && tab.id !== 'details'
                ? 'border-transparent text-gray-300 cursor-not-allowed'
                : 'border-transparent text-gray-500 hover:text-sfp-darkgray'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Details */}
      {activeTab === 'details' && (
        <div className="space-y-4 max-w-lg">
          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div>
            <label htmlFor="full_name" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Full Name *</label>
            <input id="full_name" name="full_name" value={formData.full_name} onChange={handleChange} className="input-field w-full" placeholder="e.g. John Smith" />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Email Address *</label>
            <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} className="input-field w-full" placeholder="john@sfpwealth.co.za" />
          </div>

          <div>
            <label htmlFor="mobile" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Mobile</label>
            <input id="mobile" name="mobile" value={formData.mobile} onChange={handleChange} className="input-field w-full" placeholder="+27 82 000 0000" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="licence_type" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Licence Type</label>
              <select id="licence_type" name="licence_type" value={formData.licence_type} onChange={handleChange} className="input-field w-full">
                <option value="">Select type...</option>
                {LICENCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="licence_number" className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Licence Number</label>
              <input id="licence_number" name="licence_number" value={formData.licence_number} onChange={handleChange} className="input-field w-full" placeholder="e.g. FSP12345" />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Active</span>
            <Toggle checked={formData.active} onChange={(val) => setFormData(p => ({ ...p, active: val }))} />
          </div>

          {/* Profile Photo */}
          <div className="pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium mb-3" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Profile Photo</label>
            <div className="flex items-center gap-4">
              <AdvisorAvatar
                avatarUrl={avatarPreview}
                fullName={formData.full_name}
                size="lg"
              />
              <div className="flex flex-col gap-1">
                <input
                  id="avatar_upload"
                  name="avatar_upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarChange}
                  className="input-field text-sm py-1.5"
                />
                <p className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>JPG, PNG or WebP. Shown as a bubble on all tasks.</p>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <Button variant="primary" onClick={handleSave} disabled={saving || avatarUploading}>
              {saving || avatarUploading ? 'Saving...' : savedAdvisorId ? 'Update & Next' : 'Save & Continue'}
            </Button>
          </div>
        </div>
      )}

      {/* Tab: Roles */}
      {activeTab === 'roles' && (
        <div className="space-y-2">
          {notSavedYet ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Save user details first.</p>
          ) : rolesLoading ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Loading roles...</p>
          ) : roles.length === 0 ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>No roles found. Create roles under Admin → Roles first.</p>
          ) : (
            <>
              <p className="text-xs mb-4" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Toggle to assign or remove roles for this user.</p>
              <div className="divide-y divide-gray-100">
                {roles.map(role => {
                  const assigned = assignedRoles.has(role.id)
                  return (
                    <div key={role.id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-medium" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>{role.name}</p>
                        {role.description && <p className="text-xs" style={{ color: isDarkMode ? '#94a3b8' : '#999999' }}>{role.description}</p>}
                      </div>
                      <Toggle checked={assigned} onChange={() => handleRoleToggle(role.id, assigned)} />
                    </div>
                  )
                })}
              </div>
              <div className="pt-4">
                <Button variant="primary" onClick={() => setActiveTab('auth')}>Next: Authentication</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab: Authentication */}
      {activeTab === 'auth' && (
        <div className="space-y-6 max-w-lg">
          {notSavedYet ? (
            <p className="text-sm" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>Save user details first.</p>
          ) : !isOwner ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                Only owners can manage user authentication. Contact an owner to send invitations or reset passwords.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
              {successMessage && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>User Email</h3>
                  <p className="text-sm px-3 py-2 rounded-lg" style={{ color: isDarkMode ? '#cbd5e1' : '#333333', backgroundColor: isDarkMode ? '#334155' : '#f9fafb' }}>{formData.email}</p>
                </div>

                {/* Send Invitation */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Send Invitation</h4>
                      <p className="text-xs mt-1 mb-3" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                        Send an email invitation to this user so they can set up their password and log in.
                        {inviteSent && <span className="text-green-600 ml-1">(Invitation already sent)</span>}
                      </p>
                      <Button
                        variant="secondary"
                        onClick={handleSendInvite}
                        disabled={sendingInvite || secondsUntilNextInvite > 0}
                        className="text-sm"
                      >
                        {sendingInvite ? 'Sending...' : secondsUntilNextInvite > 0 ? `Next invite in ${secondsUntilNextInvite}s` : inviteSent ? 'Resend Invitation' : 'Send Invitation Email'}
                      </Button>
                      {secondsUntilNextInvite > 0 && (
                        <p className="text-xs text-amber-600 mt-2 font-medium">
                          ⏱️ Please wait {secondsUntilNextInvite} second{secondsUntilNextInvite !== 1 ? 's' : ''} before sending another invite to avoid rate limits
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Password Reset */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-50 rounded-lg">
                      <KeyRound className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>Reset Password</h4>
                      <p className="text-xs mt-1 mb-3" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                        Send a password reset email to this user. They will receive a link to create a new password.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={handleSendPasswordReset}
                        disabled={sendingReset || secondsUntilNextReset > 0}
                        className="text-sm"
                      >
                        {sendingReset ? 'Sending...' : secondsUntilNextReset > 0 ? `Next reset in ${secondsUntilNextReset}s` : 'Send Password Reset Email'}
                      </Button>
                      {secondsUntilNextReset > 0 && (
                        <p className="text-xs text-amber-600 mt-2 font-medium">
                          ⏱️ Please wait {secondsUntilNextReset} second{secondsUntilNextReset !== 1 ? 's' : ''} before sending another reset to avoid rate limits
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* QR Code Sign-In (No Email Required) */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <QrCode className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium" style={{ color: isDarkMode ? '#e2e8f0' : '#333333' }}>QR Code Sign-In</h4>
                      <p className="text-xs mt-1 mb-3" style={{ color: isDarkMode ? '#cbd5e1' : '#666666' }}>
                        Generate a QR code that the user can scan directly in the app to log in. No email required.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={handleGenerateQRCode}
                        disabled={generatingQR || secondsUntilNextQR > 0}
                        className="text-sm"
                      >
                        {generatingQR ? 'Generating...' : secondsUntilNextQR > 0 ? `Next QR in ${secondsUntilNextQR}s` : 'Generate QR Code'}
                      </Button>
                      {secondsUntilNextQR > 0 && (
                        <p className="text-xs text-amber-600 mt-2 font-medium">
                          ⏱️ Please wait {secondsUntilNextQR} second{secondsUntilNextQR !== 1 ? 's' : ''} before generating another QR code to avoid rate limits
                        </p>
                      )}

                      {signInLink && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex flex-col items-center gap-3">
                            <div className="bg-white p-3 rounded-lg">
                              <QRCodeSVG value={signInLink} size={200} level="H" />
                            </div>
                            <div className="text-center w-full">
                              <p className="text-xs mb-2" style={{ color: isDarkMode ? '#cbd5e1' : '#333333' }}>User can scan this QR code with any phone camera</p>
                              <Button
                                variant="secondary"
                                onClick={copyLinkToClipboard}
                                className="text-xs inline-flex items-center gap-2"
                              >
                                <Copy className="w-3 h-3" />
                                Copy Link
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-3 break-all bg-white p-2 rounded border border-gray-200">
                            {signInLink}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <Button variant="primary" onClick={onClose}>Done</Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
